import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { downloadBlob, useSketchDraw } from "@penwell/draw-on";
import styles from "./DrawOnDemo.module.css";
import { ArrowUpRight, Check, Copy, Download, Replay } from "../common/Icons";
import LiquidGlass from "../common/LiquidGlass";
import Reveal from "../common/Reveal";
import SectionIntro from "../common/SectionIntro";
import { SKETCH_PALETTE } from "../common/sketchPalette";
import useDrawOnceInView from "../../hooks/useDrawOnceInView";
import { buildSketch } from "./sketch";

const PACKAGE_NAME = "@penwell/draw-on";
const INSTALL_COMMAND = `npm install ${PACKAGE_NAME}`;
const NPM_URL = "https://www.npmjs.com/package/@penwell/draw-on";
const REPO_URL = "https://github.com/penwell-app/draw-on";

/** Vivus counts in animation frames, so 260 frames is roughly four seconds. */
const DURATION = 260;
const REVEAL_START = 0.6;

const facts = [
  { label: "Published", value: "September 2026, public on npm" },
  { label: "Built on", value: "Vivus, extended to handle fills and text" },
  { label: "Solves", value: "Real diagrams are mostly filled shapes and labels, which Vivus alone leaves blank" },
  { label: "Ships", value: "ESM and CJS builds, tree-shakeable, typed, React optional" },
  { label: "Recording", value: "WebM through MediaRecorder, no server, no ffmpeg" },
  { label: "Licence", value: "MIT" },
];

const links = [
  { label: "npm package", href: NPM_URL, note: "Readme, install, version history" },
  { label: "Source on GitHub", href: REPO_URL, note: "penwell-app/draw-on" },
  { label: "First user", href: "https://usepenwell.com/", note: "Penwell, for notebook pages" },
];

const DrawOnDemo = ({ theme = "light" }) => {
  const dark = theme === "dark";
  const palette = SKETCH_PALETTE[dark ? "dark" : "light"];

  // Rebuilt per theme: this recolours the ink and makes the diagram redraw
  // itself when the theme is switched, since a new markup rebuilds the drawing.
  const markup = useMemo(() => buildSketch(palette), [palette]);

  const [progress, setProgress] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const [recording, setRecording] = useState(null);
  const [recordError, setRecordError] = useState("");
  const [copied, setCopied] = useState(false);

  const {
    containerRef,
    status,
    error,
    play,
    setProgress: scrubTo,
    canRecord,
    exportWebM,
    drawing,
  } = useSketchDraw({
    svgMarkup: markup,
    autoPlay: false,
    duration: DURATION,
    type: "oneByOne",
    revealStart: REVEAL_START,
    stroke: palette.ink,
    onDone: () => setProgress(1),
  });

  const isError = status === "error";

  // Wait until the diagram is on screen before drawing it, otherwise the whole
  // animation plays out above the fold and the visitor arrives at a still image.
  const [sectionRef, phase, setPhase] = useDrawOnceInView(markup, {
    play,
    isError,
    threshold: 0.3,
  });

  // Documented escape hatch to the engine: used to pause the play loop so a
  // scrub is not overwritten on the next animation frame.
  const drawingRef = useRef(null);
  useEffect(() => {
    if (drawing) drawingRef.current = drawing;
  }, [drawing]);

  useEffect(() => {
    setProgress(0);
    setScrubbing(false);
    setRecordError("");
  }, [markup]);

  const handlePlay = useCallback(() => {
    setScrubbing(false);
    setProgress(0);
    setPhase("drawing");
    play();
  }, [play, setPhase]);

  const handleScrub = useCallback(
    (event) => {
      const fraction = Number(event.target.value) / 100;
      drawingRef.current?.stop();
      scrubTo(fraction);
      setScrubbing(true);
      setProgress(fraction);
    },
    [scrubTo]
  );

  const handleExport = useCallback(async () => {
    setRecordError("");
    setRecording(0);

    try {
      const blob = await exportWebM({
        durationMs: DURATION * 16,
        background: dark ? "#121110" : "#fbfaf7",
        onProgress: (fraction) => setRecording(fraction),
      });
      downloadBlob(blob, "draw-on-demo.webm");
    } catch (err) {
      setRecordError(err instanceof Error ? err.message : "Recording failed.");
    } finally {
      setRecording(null);
    }
  }, [dark, exportWebM]);

  const handleCopyInstall = useCallback(() => {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(INSTALL_COMMAND);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  // The library reports "drawing" as soon as it mounts the markup, so the demo
  // tracks its own phase: an idle diagram should not claim to be drawing.
  const displayPhase = isError ? "error" : status === "done" ? "done" : phase;

  const statusText = {
    idle: "Draws when it scrolls into view",
    drawing: "Drawing the strokes",
    done: "Drawn. Try the scrubber",
    error: error || "This diagram cannot be animated",
  }[displayPhase];

  return (
    <section className={styles.container} id="drawon" ref={sectionRef}>
      <SectionIntro
        index="04"
        label="Open source"
        title="I published the SVG animation engine behind Penwell's notebook pages."
        lede="Strokes draw themselves, then the fills and labels fade in. It is scrubbable, and it exports a WebM without a server. Everything below is the real package rendering on this page."
      />

      <div className={styles.layout}>
        <Reveal className={styles.stage}>
          <LiquidGlass className={styles.panel} blur="full" wash>
            <div className={styles.panelHead}>
              <span className={styles.pkgName}>{PACKAGE_NAME}</span>
              <span className={styles.pkgMeta}>v0.1.0 &#183; MIT &#183; React optional</span>
            </div>

            <div className={styles.canvas}>
              <div ref={containerRef} className={styles.host} />
            </div>

            <div className={styles.controls}>
              <button type="button" className={styles.playBtn} onClick={handlePlay}>
                <Replay size={15} />
                <span>{displayPhase === "idle" ? "Draw it now" : "Draw it again"}</span>
              </button>

              <label className={styles.scrubber}>
                <span className={styles.scrubLabel}>setProgress</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  value={Math.round(progress * 1000) / 10}
                  onChange={handleScrub}
                  aria-label="Scrub the draw-on animation"
                />
                <span className={styles.scrubValue}>{progress.toFixed(2)}</span>
              </label>

              {canRecord && (
                <button
                  type="button"
                  className={styles.exportBtn}
                  onClick={handleExport}
                  disabled={recording !== null}
                >
                  <Download size={15} />
                  <span>
                    {recording === null
                      ? "Export WebM"
                      : `Recording ${Math.round(recording * 100)}%`}
                  </span>
                </button>
              )}
            </div>

            <p className={styles.status} aria-live="polite">
              <span className={styles.statusDot} data-status={displayPhase} aria-hidden="true" />
              <span>{statusText}</span>
              <span className={styles.statusHint}>
                {scrubbing ? "Scrubbing pauses playback" : "Fills and labels arrive after 60%"}
              </span>
            </p>
            {recordError && <p className={styles.recordError}>{recordError}</p>}
          </LiquidGlass>
        </Reveal>

        <Reveal as="aside" className={styles.notes} delay={90} aria-label="About the package">
          <div className={styles.installBlock}>
            <span className={styles.blockLabel}>Install</span>
            <div className={styles.installRow}>
              <code className={styles.installCode}>{INSTALL_COMMAND}</code>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopyInstall}
                aria-label="Copy the install command"
              >
                {copied ? <Check size={14} className={styles.copiedIcon} /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          <dl className={styles.specList}>
            {facts.map((fact) => (
              <div key={fact.label} className={styles.specRow}>
                <dt className={styles.specKey}>{fact.label}</dt>
                <dd className={styles.specValue}>{fact.value}</dd>
              </div>
            ))}
          </dl>

          <ul className={styles.linkList}>
            {links.map((link) => (
              <li key={link.href}>
                <a href={link.href} target="_blank" rel="noreferrer" className={styles.link}>
                  <span className={styles.linkBody}>
                    <span className={styles.linkLabel}>{link.label}</span>
                    <span className={styles.linkNote}>{link.note}</span>
                  </span>
                  <ArrowUpRight size={14} className={styles.linkIcon} />
                </a>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

export default DrawOnDemo;

